import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import StyledDatePicker from '@/components/DatePicker';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { modalStyle } from '@/styles/style';
import { getTimezoneDayjs } from '@/utils/time';

import type {
  ImportHistoryItemT,
  ImportRuleT,
  ImportTemplateT,
} from '../providers/types/types';
import AnalysisTriggerOptions from './AnalysisTriggerOptions';

type IntegrationImportModalProps = {
  title: string;
  subTitle: string;
  subTitleList?: string;
  historyList: ImportHistoryItemT[];
  isNew?: boolean;
  importRule?: ImportRuleT;
  template?: ImportTemplateT;
  onSaveWithoutImport?: (importRule: ImportRuleT) => void;
  onSubmitImport: (
    start_date: string,
    end_date: string,
    importRule: ImportRuleT,
  ) => void;
};

type IntercomImportConversationState = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};
// Integration 관련 Import 모달
const IntegrationImportModal = ({
  title,
  subTitle,
  subTitleList,
  historyList,
  importRule,
  template,
  isNew = false,
  onSubmitImport,
  onSaveWithoutImport,
}: IntegrationImportModalProps) => {
  const [importOptionValue, setImportOptionValue] = useState(
    importRule || {
      status: 2,
      bound: 1,
      tag: 1,
      tag_names: [],
    },
  );
  const { closeModal } = useModalContext();

  useEffect(() => {
    if (importRule) {
      setImportOptionValue(importRule);
    }
  }, [JSON.stringify(importRule)]);

  const color = useColor();

  const [state, setState] = useState<IntercomImportConversationState>({
    startDate: null,
    endDate: historyList.length ? null : getTimezoneDayjs(),
  });

  const disabledStartDate: RangePickerProps['disabledDate'] = useCallback(
    (current: Dayjs) => {
      const future = current > getTimezoneDayjs().endOf('day');
      if (!state.endDate) return future;
      else {
        const futureToEndDate = current > state.endDate.endOf('day');
        const before90DaysPriorToEndDate =
          current < state.endDate.addVal(-89, 'day').startOf('day');
        return future || before90DaysPriorToEndDate || futureToEndDate;
      }
    },
    [state.endDate],
  );

  const disabledEndDate: RangePickerProps['disabledDate'] = useCallback(
    (current: Dayjs) => {
      const future = current > getTimezoneDayjs().endOf('day');
      if (!state.startDate) return future;
      else {
        const pastToStartDate = current < state.startDate.startOf('day');
        const after90DaysFromStartDate =
          current > state.startDate.addVal(89, 'day').endOf('day');
        return future || after90DaysFromStartDate || pastToStartDate;
      }
    },
    [state.startDate],
  );

  const handleImportConversations = () => {
    if (!state.startDate || !state.endDate) return;
    onSubmitImport(
      state.startDate.format('YYYY-MM-DDTHH:mm:ssZ'),
      state.endDate.format('YYYY-MM-DDTHH:mm:ssZ'),
      importOptionValue,
    );
    closeModal();
  };

  const disabled = useMemo(() => {
    if (importOptionValue.tag === 2 || importOptionValue.tag === 3) {
      if (
        !importOptionValue.tag_names ||
        importOptionValue.tag_names.length < 1
      ) {
        return true;
      }
    }
    if (!state.startDate || !state.endDate) {
      return true;
    }
    return false;
  }, [state.startDate, state.endDate, importOptionValue]);

  const withOutDisabled = useMemo(() => {
    if (importOptionValue.tag === 2 || importOptionValue.tag === 3) {
      if (
        !importOptionValue.tag_names ||
        importOptionValue.tag_names.length < 1
      ) {
        return true;
      }
    }

    return false;
  }, [importOptionValue]);

  const datePickerStyle = useMemo(
    () => ({
      border: `1px solid ${color.gray400}`,
      borderRadius: '8px',
    }),
    [color.gray400],
  );

  const handleStartDateChange = useCallback((date: Dayjs) => {
    setState((prev) => ({
      ...prev,
      startDate: date,
    }));
  }, []);

  const handleEndDateChange = useCallback((date: Dayjs) => {
    setState((prev) => ({
      ...prev,
      endDate: date,
    }));
  }, []);

  const getPopupContainer = useCallback((triggerNode: HTMLElement) => {
    return ref?.current as HTMLDivElement;
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  return (
    <ModalStyle d="column" ref={ref}>
      <Flex d="column" g="32px">
        <Heading type="h2">{title}</Heading>
        <Flex d="column" g="8px">
          <Heading type="h3">{subTitle}</Heading>
          {subTitleList?.length && (
            <>
              <Divider direction="h" />
              <Typo type="label2">{subTitleList}</Typo>
              <Divider direction="h" />
            </>
          )}
          <Typo type="label3" color={color.gray600}>
            * Newly created chats will be automatically imported to Syncly.
          </Typo>
        </Flex>
        <Flex d="column" g="32px">
          {isNew && template && (
            <AnalysisTriggerOptions
              importRule={importOptionValue}
              setImportRule={setImportOptionValue}
              template={template}
            />
          )}
          <Flex d="column" g="8px">
            <Heading type="h3">Date</Heading>
            <Flex d="column" g="4px">
              <Typo type="label3" color={color.gray600}>
                This is a one-time import. You can repeat the process with a
                different date range.
              </Typo>
              <Typo type="label3" color={color.gray600}>
                The maximum duration is 90 days.
              </Typo>
            </Flex>
            <Flex a="center" g="12px">
              <StyledDatePicker
                style={datePickerStyle}
                name="startDate"
                onChange={handleStartDateChange}
                disabledDate={disabledStartDate}
                getPopupContainer={getPopupContainer}
                value={state.startDate}
              />
              <Typo type="label2" color={color.gray600}>
                -
              </Typo>
              {historyList.length ? (
                <StyledDatePicker
                  style={datePickerStyle}
                  name="endDate"
                  onChange={handleEndDateChange}
                  disabledDate={disabledEndDate}
                  getPopupContainer={getPopupContainer}
                  value={state.endDate}
                />
              ) : (
                <Typo type="label2" color={color.gray600}>
                  Today
                </Typo>
              )}
            </Flex>
          </Flex>
          {historyList.length > 0 && (
            <Flex d="column" g="8px">
              <Heading type="h3">One-time import history</Heading>
              <div className="flex flex-col overflow-y-scroll max-h-100">
                {historyList.map((historyItem) => {
                  const startDate = getTimezoneDayjs(
                    historyItem.start_date,
                  ).format('YYYY/MM/DD');
                  const endDate = getTimezoneDayjs(historyItem.end_date).format(
                    'YYYY/MM/DD',
                  );
                  const createdDate = getTimezoneDayjs(
                    historyItem.created_at,
                  ).format('YYYY/MM/DD');
                  return (
                    <React.Fragment key={historyItem.created_at}>
                      <Divider direction="h" />
                      <div className="flex justify-between shrink-0 py-2">
                        <Typo type="label2">
                          From {startDate} to {endDate}
                        </Typo>
                        <Typo type="label3" color={color.gray600}>
                          imported on {createdDate}
                        </Typo>
                      </div>
                    </React.Fragment>
                  );
                })}
                <Divider direction="h" />
              </div>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Space h="52px" />
      <Flex j="flex-end" g="6px">
        {isNew ? (
          <Button
            sizeType="m"
            variant="ghost"
            onClick={() => {
              onSaveWithoutImport?.(importOptionValue);
              closeModal();
            }}
            disabled={withOutDisabled}
          >
            Save without importing
          </Button>
        ) : (
          <Button
            sizeType="m"
            variant="ghost"
            onClick={() => {
              closeModal();
            }}
          >
            Cancel
          </Button>
        )}

        <Button
          sizeType="m"
          onClick={handleImportConversations}
          disabled={disabled}
        >
          Import
        </Button>
      </Flex>
    </ModalStyle>
  );
};

export default IntegrationImportModal;

const ModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  ${modalStyle};
  max-height: 90vh;
  overflow-y: auto;
`;
